import { canv } from "@/canv/global";
import type { TextWidgetV2 } from "../../textV2";
import type { ScenePoint, SceneRect } from "../types";

function rotatePoint(point: ScenePoint, center: ScenePoint, angleDeg: number): ScenePoint {
    if (!angleDeg) {
        return point;
    }

    const angleRad = (angleDeg * Math.PI) / 180;
    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const dx = point.x - center.x;
    const dy = point.y - center.y;

    return {
        x: center.x + dx * cos - dy * sin,
        y: center.y + dx * sin + dy * cos,
    };
}

function getWidgetSceneOrigin(widget: TextWidgetV2): ScenePoint {
    return {
        x: widget.startX,
        y: widget.startY - widget.lineHeight,
    };
}

function getWidgetSceneCenter(widget: TextWidgetV2): ScenePoint {
    const origin = getWidgetSceneOrigin(widget);

    return {
        x: origin.x + widget.width / 2,
        y: origin.y + widget.height / 2,
    };
}

function getWidgetAngle(widget: TextWidgetV2): number {
    const parentAngle = typeof widget.parent?.angle === "number" ? widget.parent.angle : 0;
    return -parentAngle;
}

export function pointInRect(pt: ScenePoint, rect: SceneRect): boolean {
    return (
        pt.x >= rect.x &&
        pt.x <= rect.x + rect.width &&
        pt.y >= rect.y &&
        pt.y <= rect.y + rect.height
    );
}

export function sceneToClientPoint(x: number, y: number): ScenePoint {
    const deviceX = canv.m[0] * x + canv.m[2] * y + canv.m[4];
    const deviceY = canv.m[1] * x + canv.m[3] * y + canv.m[5];

    return {
        x: deviceX / canv.dpr,
        y: deviceY / canv.dpr,
    };
}

export function clientToScenePoint(x: number, y: number): ScenePoint {
    const [a, b, c, d, e, f] = canv.m;

    const det = a * d - b * c;
    if (Math.abs(det) < 1e-8) {
        return { x: 0, y: 0 };
    }

    const deviceX = x * canv.dpr;
    const deviceY = y * canv.dpr;

    const tx = deviceX - e;
    const ty = deviceY - f;

    return {
        x: (d * tx - c * ty) / det,
        y: (-b * tx + a * ty) / det,
    };
}

export function localPointToScene(widget: TextWidgetV2, localPoint: ScenePoint): ScenePoint {
    const origin = getWidgetSceneOrigin(widget);
    const center = getWidgetSceneCenter(widget);
    const angle = getWidgetAngle(widget);

    const untranslated = {
        x: origin.x + localPoint.x,
        y: origin.y + localPoint.y,
    };

    return rotatePoint(untranslated, center, angle);
}

export function scenePointToLocal(widget: TextWidgetV2, scenePoint: ScenePoint): ScenePoint {
    const origin = getWidgetSceneOrigin(widget);
    const center = getWidgetSceneCenter(widget);
    const angle = getWidgetAngle(widget);

    const unrotated = rotatePoint(scenePoint, center, -angle);

    return {
        x: unrotated.x - origin.x,
        y: unrotated.y - origin.y,
    };
}

export function invertWidgetTransform(widget: TextWidgetV2, scenePoint: ScenePoint): ScenePoint {
    return scenePointToLocal(widget, scenePoint);
}

export function localRectToSceneQuad(widget: TextWidgetV2, rect: SceneRect): ScenePoint[] {
    return [
        localPointToScene(widget, { x: rect.x, y: rect.y }),
        localPointToScene(widget, { x: rect.x + rect.width, y: rect.y }),
        localPointToScene(widget, { x: rect.x + rect.width, y: rect.y + rect.height }),
        localPointToScene(widget, { x: rect.x, y: rect.y + rect.height }),
    ];
}

export function quadToClientDOMRect(points: ScenePoint[]): DOMRect {
    const clientPoints = points.map((pt) => sceneToClientPoint(pt.x, pt.y));

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const pt of clientPoints) {
        if (pt.x < minX) minX = pt.x;
        if (pt.y < minY) minY = pt.y;
        if (pt.x > maxX) maxX = pt.x;
        if (pt.y > maxY) maxY = pt.y;
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY)) {
        return DOMRect.fromRect({ x: 0, y: 0, width: 0, height: 0 });
    }

    return DOMRect.fromRect({
        x: minX,
        y: minY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY),
    });
}
